import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { posthogClient } from 'lib/analytics/posthog'
import { LocalStorageKeysEnum } from 'types/localStorage'

import {
	API_ROUTES,
	AUTH_TOKEN_COOKIE,
	DEFAULT_LOCALE,
	PURCHASE_SELECTED_PRODUCT_COOKIE,
} from '@/constants'
import { userAtom } from '@/global-state'
import { getStore } from '@/store'
import { env, isOnServer } from '@/utils'
import {
	deleteCookieClient,
	getCookieClient,
	setCookieClient,
} from '@/utils/client'

import { addAxiosLoggingInterceptor } from './axiosLoggingInterceptor'

const ROLE_COOKIE = 'role'

const REFRESH_EXEMPT_AUTH_ROUTES = [
	'/auth/login',
	'/auth/signup',
	'/auth/google',
	'/auth/facebook',
	'/auth/apple',
	'/auth/logout',
]

// Cookie-based helpers — do NOT import getAuthToken/setAuthToken from
// features/auth/atoms. That module is `'use client'`, so Next.js replaces
// those exports with client-reference proxies on the server; calling them
// in production SSR throws `TypeError: i is not a function`.
const getAuthToken = (): string | null => {
	if (isOnServer) return null
	return getCookieClient(AUTH_TOKEN_COOKIE) ?? null
}

export const setAuthToken = (token: string | null, role?: string) => {
	if (isOnServer) return

	if (token) {
		setCookieClient(AUTH_TOKEN_COOKIE, token, { days: 7 })
		if (role) {
			setCookieClient(ROLE_COOKIE, role, { days: 7 })
		}
	} else {
		deleteCookieClient(AUTH_TOKEN_COOKIE)
		deleteCookieClient(ROLE_COOKIE)
	}
}

export const signOut = async ({
	isRedirecting = true,
}: {
	isRedirecting: boolean
}) => {
	const currentLocale = getStore().get(userAtom)?.language || DEFAULT_LOCALE

	setAuthToken(null)

	if (isOnServer) {
		try {
			const { cookies } = await import('next/headers')
			const cookieStore = await cookies()
			cookieStore.delete(AUTH_TOKEN_COOKIE)
		} catch (error) {
			console.error('[auth] Could not delete auth cookie on the server', {
				message: error instanceof Error ? error.message : String(error),
			})
		}
	} else {
		deleteCookieClient(AUTH_TOKEN_COOKIE)
		window.localStorage.removeItem(LocalStorageKeysEnum.ACTIVE_PRODUCT)
		deleteCookieClient(PURCHASE_SELECTED_PRODUCT_COOKIE)
		// MYQRCODE-10778 AC#9: the single chokepoint for ending a session —
		// explicit logout, account deletion, and forced logout on refresh
		// failure (below) all funnel through here.
		posthogClient.reset()
	}

	getStore().set(userAtom, null)

	if (isRedirecting && !isOnServer) {
		const redirectPath =
			currentLocale === DEFAULT_LOCALE ? '/' : `/${currentLocale}/`
		window.location.href = redirectPath
	}
}

const apiClient = axios.create({
	baseURL: isOnServer ? env.INTERNAL_API_URL : env.API_URL,
	timeout: 120_000,
	timeoutErrorMessage: 'Timeout Error',
})

export const getApiClient = () => apiClient

if (isOnServer && env.ENABLE_AXIOS_LOGS_ON_SERVER) {
	addAxiosLoggingInterceptor(apiClient)
}

apiClient.interceptors.request.use(
	async (config) => {
		config.headers = config.headers || {}
		if (!isOnServer) {
			const authToken = getCookieClient(AUTH_TOKEN_COOKIE)
			if (authToken) {
				config.headers.Authorization = `Bearer ${authToken}`
			}
			const locale = getCookieClient('lang') || DEFAULT_LOCALE
			config.headers['x-locale'] = locale
		} else {
			const { cookies } = await import('next/headers')
			const cookieStore = await cookies()
			const authToken = cookieStore.get(AUTH_TOKEN_COOKIE)?.value
			if (authToken) {
				config.headers.Authorization = `Bearer ${authToken}`
			}
			const locale = cookieStore.get('lang')?.value || DEFAULT_LOCALE
			config.headers['x-locale'] = locale
		}
		return config
	},

	(error) => {
		return Promise.reject(error)
	},
)

// Refresh access token function
export const refreshAccessToken = async (): Promise<string> => {
	// Get the current user from the store
	const currentUser = getStore().get(userAtom)
	if (!currentUser) {
		return ''
	}

	// Send the user in the request body
	const response = await apiClient.post(
		API_ROUTES.auth.refreshToken,
		{ user: currentUser, accessToken: getAuthToken() }, // Include user in the body
		{ withCredentials: true },
	)

	return response.data.accessToken
}

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const isUnauthorized = error.response?.status === 401

		if (!isUnauthorized || isOnServer) {
			return Promise.reject(error)
		}

		const isFailedRefreshTokenRequest =
			error.config?.url === API_ROUTES.auth.refreshToken

		const isRefreshExemptRoute = REFRESH_EXEMPT_AUTH_ROUTES.some((route) =>
			error.config?.url?.includes(route),
		)

		if (isFailedRefreshTokenRequest) {
			await signOut({ isRedirecting: true })
			return Promise.reject(error)
		}

		if (isRefreshExemptRoute) {
			return Promise.reject(error)
		}

		try {
			// MYQRCODE-10838: refreshAccessToken() returns '' both when a real
			// user's refresh genuinely fails AND when there was never a user
			// to refresh (a guest). Only the former is an actual session
			// ending — signing out (and posthogClient.reset()-ing) a guest who
			// was never signed in was silently forking their PostHog identity
			// on any unrelated 401 hit while browsing.
			const hadUser = Boolean(getStore().get(userAtom))
			const newToken = await refreshAccessToken()

			if (!newToken) {
				if (hadUser) {
					console.error(
						'[auth] Clearing session after a failed refresh',
						{
							url: error.config?.url,
							isOnServer,
							hasUserInStore: Boolean(getStore().get(userAtom)),
						},
					)
					await signOut({ isRedirecting: false })
				}
				return Promise.reject(error)
			}

			setAuthToken(newToken)

			// Retry the original request with the new token
			if (error.config?.headers) {
				error.config.headers.Authorization = `Bearer ${newToken}`
			}
			return apiClient(error.config as AxiosRequestConfig)
		} catch (refreshError) {
			return Promise.reject(refreshError)
		}
	},
)
